import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const TEMPLATE_HEADERS = [
  "name_id", "name_en", "tier", "category", "duration", "price_idr",
  "airline", "airport", "departure_date", "departure", "ustadz",
  "hotel_makkah", "hotel_madinah", "seats_remaining", "featured", "description",
];

const SAMPLE_ROW = [
  "Umroh Silver 9 Hari", "Umrah Silver 9 Days", "silver", "umrah", "9 Hari", "31999000",
  "Garuda Indonesia", "CGK", "2026-11-24", "Jakarta", "Ustadz Abu Umair",
  "Snood Ajyad / Setaraf", "Ansar GT / Setaraf", "40", "false",
  "<p>Program Umrah 9 Hari direct flight Madinah dengan Garuda Indonesia.</p>",
];

function downloadTemplateCSV() {
  const rows = [TEMPLATE_HEADERS.join(","), SAMPLE_ROW.map((v) => `"${v}"`).join(",")];
  const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "template_paket_batik.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExcelImportCard({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("File harus .xlsx");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/packages/import-xlsx", fd);
      setResult(data);
      toast.success(`Import selesai: ${data.inserted} baru, ${data.updated} diperbarui`);
      onImported?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Import gagal: ${msg}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="excel-import-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-accent/15 text-accent grid place-items-center shrink-0">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg text-primary">Import Paket dari Excel</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Unggah file <code>.xlsx</code> untuk menambah / memperbarui paket secara massal. Kolom minimal:{" "}
            <span className="text-primary font-medium">name_id, tier, duration, price_idr, airline, airport, departure_date</span>.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              onChange={onFile}
              className="hidden"
              data-testid="excel-import-input"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid="excel-import-btn"
              className="inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? "Mengimpor…" : "Pilih File .xlsx"}
            </Button>
            <button
              type="button"
              onClick={downloadTemplateCSV}
              data-testid="excel-template-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-accent/10"
            >
              <Download className="h-4 w-4" /> Template CSV
            </button>
          </div>

          {result && (
            <div className="mt-4 border border-green-200 bg-green-50 rounded-md p-3 text-sm" data-testid="import-result">
              <p className="flex items-center gap-2 text-green-800 font-medium">
                <CheckCircle2 className="h-4 w-4" /> Import selesai
              </p>
              <p className="mt-1 text-green-700">
                {result.inserted} paket baru • {result.updated} paket diperbarui
                {result.errors?.length > 0 && ` • ${result.errors.length} error`}
              </p>
              {result.errors?.length > 0 && (
                <details className="mt-2 text-xs text-red-700">
                  <summary className="cursor-pointer">Detail error</summary>
                  <ul className="mt-1 list-disc list-inside">
                    {result.errors.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
